import React, { ReactNode, useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import { Flex } from '@reckon-web/flex';
import { LoadingDots } from '@reckon-web/loading';

import { getUserPoolClientData } from '../utils/helper';
import { configureAmplifyAuth } from '../utils/auth';
import { mfaTokenPrefix } from '../constants';

export const IdContext = React.createContext<any>({});

export const IdContextProvider = ({ children }: { children: ReactNode }) => {
  const router = useRouter();
  const [clientData, setClientData] = useState({
    clientId: '',
    redirectURL: '',
    tenancy: '',
    product: '',
    clientState: '',
  });

  const [sessionMfa, setSessionMfa] =
    useState<Record<'storageKey' | 'token', string | undefined>>();

  useEffect(() => {
    const checkValidReq = (tenancy: string, pathname: string) => {
      if (
        process.env.STAGE !== 'dev' &&
        ((router.asPath.includes('client_id') && router?.query?.client_id) ||
          !router.asPath.includes('client_id'))
      ) {
        if (tenancy !== 'APS') {
          throw new Error('Bad request');
        } else if (
          tenancy === 'APS' &&
          ![
            '/',
            '/login',
            '/forgot-password',
            '/reset-password',
            '/signout',
            '/mfa',
            '/verifycode',
            '/mfa-recovery',
          ].includes(pathname)
        ) {
          throw new Error('Bad request');
        }
      } else if (
        (router.asPath.includes('client_id') && router?.query?.client_id) ||
        !router.asPath.includes('client_id')
      ) {
        if (!tenancy) throw new Error('Bad request');
      }
    };

    if (!clientData?.clientId) {
      const [wClientId, wRedirect, wTenancy, wProduct, wState] =
        getUserPoolClientData(router);

      checkValidReq(wTenancy || '', router.pathname);
      const storageKey = `${mfaTokenPrefix}.${wClientId || ''}.token`;

      const newSessionMfa = {
        storageKey,
        token:
          typeof window !== 'undefined'
            ? localStorage.getItem(storageKey) || undefined
            : undefined,
      };

      if (wClientId) {
        const newClientData = {
          clientId: wClientId || '',
          redirectURL: wRedirect || '',
          tenancy: wTenancy || '',
          product: wProduct || '',
          clientState: wState || '',
        };

        setClientData(newClientData);
        setSessionMfa(newSessionMfa);
        configureAmplifyAuth(wClientId);
      }
    } else {
      checkValidReq(clientData?.tenancy || '', router.pathname);
    }
  }, [
    router.asPath,
    router.pathname,
    router.query,
    clientData?.clientId,
    clientData?.tenancy,
    router,
  ]); // Only depend on specific router properties and clientId

  if (!clientData?.clientId) {
    return (
      <Flex justifyContent="center" alignItems="center" height="100vh">
        <LoadingDots label="Intial data loading" size="large" />
      </Flex>
    );
  }

  return (
    <IdContext.Provider value={{ ...clientData, sessionMfa }}>
      {children}
    </IdContext.Provider>
  );
};
